import { createPortal } from 'react-dom';
import styles from './MentionChipHoverPreview.module.css';

/** アンカー (チップ) の viewport 座標・サイズ。getBoundingClientRect と同じ座標系。 */
export type MentionChipHoverAnchor = { top: number; left: number; width: number; height: number };

interface MentionChipHoverPreviewProps {
  anchor: MentionChipHoverAnchor;
  thumbnailUrl: string | null;
  name: string | null;
  kind: 'material' | 'actor';
}

const PREVIEW_SIZE = 120;
const GAP = 6;
const VIEWPORT_MARGIN = 8;
// 上に出す余地があるかどうかの判定用の見込み高さ (画像120 + 名前1行 + padding)。
// 実測はしない (レイアウト後の再測定は表示位置のちらつきにつながるため)。位置決め
// 自体は translateY(-100%) で anchor 上辺を基準にするので、この定数が多少ズレても
// 見た目のズレには影響しない (上/下どちらに出すかの判定にのみ使う)。
const ESTIMATED_BOX_HEIGHT = 150;

/**
 * @メンショントークンのチップにホバーしたときの拡大サムネイルプレビュー
 * (docs/features/material-at-mention-plan.md「トークンのチップ表示」節)。
 * 選択素材パネルの `.assetPreview`
 * ([GenerationWorkspace.module.css](../GenerationWorkspace.module.css) 120x120・角丸12px)
 * と同じサイズ・トーンで、document.body へ portal 表示する
 * ([MaterialMentionPopup.tsx](./MaterialMentionPopup.tsx) と同じ様式。overflow クリップ回避・
 * z-index 揃え)。
 *
 * **常に `pointer-events:none`**: 編集中の textarea 操作 (mousemove ヒットテスト方式。
 * [MentionTokenChipOverlay.tsx](./MentionTokenChipOverlay.tsx)) や読み取り表示のクリックを
 * 一切妨げない。副次的に、プレビュー自体にマウスが乗って `mouseleave` が発火しない
 * 問題も避けられる。
 */
export function MentionChipHoverPreview({ anchor, thumbnailUrl, name, kind }: MentionChipHoverPreviewProps) {
  const centerX = anchor.left + anchor.width / 2;
  const left = Math.max(
    VIEWPORT_MARGIN,
    Math.min(centerX - PREVIEW_SIZE / 2, window.innerWidth - PREVIEW_SIZE - VIEWPORT_MARGIN),
  );

  // 上に十分な余地が無ければ下辺側へ出す。translateY で anchor の上辺/下辺のどちらを
  // 基準にするかだけを切り替えるため、実際のボックス高さが定数と多少ズレても崩れない。
  const showBelow = anchor.top < ESTIMATED_BOX_HEIGHT + GAP + VIEWPORT_MARGIN;
  const top = showBelow ? anchor.top + anchor.height + GAP : anchor.top - GAP;
  const transform = showBelow ? undefined : 'translateY(-100%)';

  return createPortal(
    <div className={styles.preview} style={{ top, left, transform }} aria-hidden="true">
      <div className={`${styles.thumb} ${kind === 'actor' ? styles.thumbActor : ''}`}>
        {thumbnailUrl && <img src={thumbnailUrl} alt="" draggable={false} />}
      </div>
      {name && <div className={styles.name}>{name}</div>}
    </div>,
    document.body,
  );
}
